import { Link, useParams } from 'react-router-dom'
import { useAsync } from '../hooks/useAsync'
import { fetchGame } from '../services/league'
import {
  Avatar,
  Card,
  ConferenceBadge,
  EmptyState,
  Loading,
  Notice,
  PlayoffBadge,
  formatDate,
  playerProfilePath,
} from '../components/ui'
import { TeamHelmetLabel } from '../components/TeamHelmet'
import { TeamBoxScore } from '../components/TeamBoxScore'
import { EXTRA_STATS_FROM_SEASON } from '../lib/teamBoxScore'
import { resolveQuarterBreakdown } from '../lib/quarterScoring'
import type { GameMetadata, GameSide, PlayerStatLine, TeamStatSummary } from '../types'
import './GameDetails.css'

interface StatRow {
  slot: string
  name: string
  values: Array<string | number>
}

interface StatCategory {
  key: string
  label: string
  columns: string[]
  rows: (team: TeamStatSummary) => StatRow[]
}

function playerName(player: PlayerStatLine): string {
  return player.name ?? player.slot
}

/**
 * The stat categories shown for both teams, in display order.
 *
 * @remarks
 * Categories are data rather than JSX so that a single category can be rendered
 * twice — once per team — from one definition. That is what keeps the two
 * columns of the matchup aligned: both sides always emit the same header row
 * and the same number of columns, whatever each roster actually did.
 */
function boxScoreCategories(seasonNumber: number): StatCategory[] {
  const showTackles = seasonNumber >= EXTRA_STATS_FROM_SEASON
  return [
    {
    key: 'passing',
    label: 'Passing',
    columns: ['C/A', 'Yds', 'TD', 'INT'],
    rows: team =>
      team.players
        .filter(player => (player.passing?.attempts ?? 0) > 0)
        .map(player => ({
          slot: player.slot,
          name: playerName(player),
          values: [
            `${player.passing!.completions}/${player.passing!.attempts}`,
            player.passing!.yards,
            player.passing!.touchdowns,
            player.passing!.interceptions,
          ],
        })),
    },
    {
    key: 'rushing',
    label: 'Rushing',
    columns: ['Att', 'Yds', 'TD'],
    rows: team =>
      team.players
        .filter(player => (player.rushing?.attempts ?? 0) > 0)
        .map(player => ({
          slot: player.slot,
          name: playerName(player),
          values: [
            player.rushing!.attempts,
            player.rushing!.yards,
            player.rushing!.touchdowns,
          ],
        })),
    },
    {
    key: 'receiving',
    label: 'Receiving',
    columns: ['Rec', 'Yds', 'TD'],
    rows: team =>
      team.players
        .filter(player => (player.receiving?.receptions ?? 0) > 0)
        .map(player => ({
          slot: player.slot,
          name: playerName(player),
          values: [
            player.receiving!.receptions,
            player.receiving!.yards,
            player.receiving!.touchdowns,
          ],
        })),
    },
    {
    key: 'defense',
    label: 'Defense',
    columns: showTackles ? ['Tackles', 'Sacks', 'INT'] : ['Sacks', 'INT'],
    rows: team =>
      team.players
        .filter(player => {
          const defense = player.defense
          if (!defense) return false
          return (
            defense.sacks +
              defense.interceptions +
              (showTackles ? defense.tackles : 0) >
            0
          )
        })
        .map(player => ({
          slot: player.slot,
          name: playerName(player),
          values: showTackles
            ? [player.defense!.tackles, player.defense!.sacks, player.defense!.interceptions]
            : [player.defense!.sacks, player.defense!.interceptions],
        })),
    },
  ]
}

/**
 * One team's stat lines for one category.
 *
 * @remarks
 * Deliberately not sortable. Two of these sit side by side and are meant to be
 * read across, which only works while both columns keep the game's own roster
 * order; sorting one of them independently breaks the comparison the layout
 * exists for. The tables are also tiny — a handful of ball carriers — so there
 * is nothing to search through.
 */
function StatColumn({
  side,
  category,
  rows,
}: {
  side: GameSide
  category: StatCategory
  rows: StatRow[]
}) {
  return (
    <div className="matchup__column">
      <div className="matchup__owner">
        <TeamHelmetLabel abbreviation={side.teamAbbreviation} size="sm" />
        <span className="matchup__coach">{side.displayName ?? 'Unassigned'}</span>
      </div>
      {rows.length > 0 ? (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Player</th>
                {category.columns.map(column => (
                  <th key={column} className="num">
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.slot}>
                  <td>{row.name}</td>
                  {row.values.map((value, index) => (
                    <td key={category.columns[index]} className="num">
                      {value}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="matchup__none">No {category.label.toLowerCase()} recorded.</p>
      )}
    </div>
  )
}

/**
 * Both teams' individual stat lines, one team per column.
 *
 * @remarks
 * Rendering is driven per category rather than per team so that equivalent
 * categories sit on the same row and can be read across. Returns null when
 * neither roster produced a single stat line, which is the case for every
 * historical workbook import.
 */
function MatchupStats({
  home,
  away,
  homeSide,
  awaySide,
  seasonNumber,
}: {
  home: TeamStatSummary
  away: TeamStatSummary
  homeSide: GameSide
  awaySide: GameSide
  seasonNumber: number
}) {
  const categories = boxScoreCategories(seasonNumber).map(category => ({
    category,
    homeRows: category.rows(home),
    awayRows: category.rows(away),
  })).filter(entry => entry.homeRows.length > 0 || entry.awayRows.length > 0)

  if (categories.length === 0) {
    return null
  }

  return (
    <Card title="Player stats" flush>
      <div className="matchup">
        {categories.map(({ category, homeRows, awayRows }) => (
          <section className="matchup__category" key={category.key}>
            <h3 className="matchup__heading">{category.label}</h3>
            <div className="matchup__columns">
              <StatColumn side={homeSide} category={category} rows={homeRows} />
              <StatColumn side={awaySide} category={category} rows={awayRows} />
            </div>
          </section>
        ))}
      </div>
    </Card>
  )
}

/**
 * The quarter-by-quarter line score, shown only when it agrees with the final.
 *
 * @remarks
 * When the breakdown cannot be reconciled the table is replaced by a short
 * explanation rather than a row of zeros, so a save state that failed to record
 * scoring is never mistaken for a team that failed to score.
 *
 * Vanilla Tecmo overtime is the other half of that: the ROM reuses Q1–Q4 for
 * the extra period, so those four cells are em dashes and the OT column holds
 * what the buffer actually contained. A second overtime, merged from a second
 * save, adds a 2OT column.
 *
 * Not sortable: it is exactly two rows, home above away, and the quarters
 * across the top are a timeline rather than a ranking.
 */
export function ScoringTable({ metadata }: { metadata: GameMetadata }) {
  const breakdown = resolveQuarterBreakdown(metadata.home, metadata.away)

  if (breakdown.kind === 'unavailable') {
    return (
      <p className="scoring-note">
        Quarter-by-quarter scoring wasn&rsquo;t recorded in this save state, so only the final
        score is shown.
      </p>
    )
  }

  const overtime = breakdown.kind === 'overtime'
  const doubleOvertime =
    overtime && breakdown.homeSecondOvertime != null && breakdown.awaySecondOvertime != null
  const quartersLost = overtime && breakdown.home === null
  const sides =
    breakdown.kind === 'overtime'
      ? [
          {
            team: metadata.home,
            quarters: breakdown.home,
            ot: breakdown.homeOvertime,
            ot2: breakdown.homeSecondOvertime,
          },
          {
            team: metadata.away,
            quarters: breakdown.away,
            ot: breakdown.awayOvertime,
            ot2: breakdown.awaySecondOvertime,
          },
        ]
      : [
          { team: metadata.home, quarters: breakdown.home, ot: 0, ot2: undefined },
          { team: metadata.away, quarters: breakdown.away, ot: 0, ot2: undefined },
        ]

  return (
    <div className="scoring-table">
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Scoring</th>
              <th className="num">Q1</th>
              <th className="num">Q2</th>
              <th className="num">Q3</th>
              <th className="num">Q4</th>
              {overtime && <th className="num">OT</th>}
              {doubleOvertime && <th className="num">2OT</th>}
              <th className="num">Final</th>
            </tr>
          </thead>
          <tbody>
            {sides.map(({ team, quarters, ot, ot2 }) => (
              <tr key={team.abbreviation}>
                <td>
                  <strong>{team.abbreviation}</strong>
                </td>
                {Array.from({ length: 4 }, (_, quarter) => (
                  <td key={quarter} className="num">
                    {quarters ? quarters[quarter] : '—'}
                  </td>
                ))}
                {overtime && <td className="num">{ot}</td>}
                {doubleOvertime && <td className="num">{ot2}</td>}
                <td className="num">
                  <strong>{team.score}</strong>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {quartersLost && (
        <p className="scoring-note">
          Tecmo overwrites the quarter scoreboard when overtime starts, so only overtime scoring
          is in this save. Regulation ended {breakdown.regulationTiedAt}&ndash;
          {breakdown.regulationTiedAt}.
        </p>
      )}
      {doubleOvertime && (
        <p className="scoring-note">
          Combined from two save states: regulation through first overtime, then a second game for
          2OT.
        </p>
      )}
    </div>
  )
}

/**
 * @remarks
 * The name links to that coach's career only once the game has been assigned to
 * somebody. A pending upload has scores and no players yet, and there is no
 * profile to send a reader to.
 */
function SideHeader({ side, align }: { side: GameSide; align: 'left' | 'right' }) {
  return (
    <div className={`side-header side-header--${align}`}>
      <Avatar
        name={side.displayName}
        discordId={side.discordId}
        avatar={side.discordAvatar}
        size="lg"
      />
      <div>
        <div className="side-header__name">
          {side.leaguePlayerId === null ? (
            (side.displayName ?? 'Unassigned')
          ) : (
            <Link className="player__name--link" to={playerProfilePath(side.leaguePlayerId)}>
              {side.displayName ?? 'Unassigned'}
            </Link>
          )}
        </div>
        <div className="side-header__team">
          <TeamHelmetLabel abbreviation={side.teamAbbreviation} size="md" />
        </div>
      </div>
    </div>
  )
}

export default function GameDetails() {
  const { id } = useParams<{ id: string }>()
  const game = useAsync(() => fetchGame(Number(id)), [id])

  if (game.loading) {
    return (
      <Card title="Loading game">
        <Loading />
      </Card>
    )
  }

  if (game.error || !game.data) {
    return <Notice tone="error">{game.error ?? 'That game could not be found.'}</Notice>
  }

  const detail = game.data
  const homeWon = detail.home.score > detail.away.score
  const awayWon = detail.away.score > detail.home.score

  return (
    <div className="stack">
      <header className="page-header">
        <div>
          <span className="eyebrow">
            Season {detail.seasonNumber} · {formatDate(detail.playedAt ?? detail.uploadedAt)}
          </span>
          <h1>Game #{detail.id}</h1>
        </div>
        <div className="row">
          <ConferenceBadge conference={detail.conference} />
          <PlayoffBadge isPlayoff={detail.isPlayoff} />
          {detail.status !== 'official' && (
            <span className="badge badge--pending">{detail.status}</span>
          )}
          <Link className="btn btn--ghost btn--sm" to="/games">
            Back to games
          </Link>
        </div>
      </header>

      <Card>
        <div className="final-score">
          <SideHeader side={detail.home} align="left" />
          <div className="final-score__numbers">
            <span className={homeWon ? 'final-score__value' : 'final-score__value is-loser'}>
              {detail.home.score}
            </span>
            <span className="final-score__label">Final</span>
            <span className={awayWon ? 'final-score__value' : 'final-score__value is-loser'}>
              {detail.away.score}
            </span>
          </div>
          <SideHeader side={detail.away} align="right" />
        </div>

        {detail.metadata && <ScoringTable metadata={detail.metadata} />}
      </Card>

      {(detail.notes || detail.boxScoreUrl) && (
        <Card title="Recap">
          {detail.notes && <p className="game-notes">{detail.notes}</p>}
          {detail.boxScoreUrl && (
            <a
              className="game-notes__link"
              href={detail.boxScoreUrl}
              target="_blank"
              rel="noreferrer noopener"
            >
              View the final-score screenshot
            </a>
          )}
        </Card>
      )}

      {detail.metadata ? (
        <>
          <div className="matchup-pair">
            <Card>
              <TeamBoxScore
                team={detail.metadata.home}
                venue="home"
                seasonNumber={detail.seasonNumber}
              />
            </Card>
            <Card>
              <TeamBoxScore
                team={detail.metadata.away}
                venue="away"
                seasonNumber={detail.seasonNumber}
              />
            </Card>
          </div>
          <MatchupStats
            home={detail.metadata.home}
            away={detail.metadata.away}
            homeSide={detail.home}
            awaySide={detail.away}
            seasonNumber={detail.seasonNumber}
          />
        </>
      ) : (
        <Card>
          <EmptyState title="No box score for this game">
            This result came from a historical import, so only the final score is on record.
          </EmptyState>
        </Card>
      )}
    </div>
  )
}
